import AddIcon from '@material-design-icons/svg/filled/add.svg?react';
import CloudUploadIcon from '@material-design-icons/svg/filled/cloud_upload.svg?react';
import LinkIcon from '@material-design-icons/svg/filled/link.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import { AnimatePresence, motion, type HTMLMotionProps } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import {
  APK_EXTENSION_ERROR_MESSAGE,
  hasApkFileExtension,
} from '../../utils/apkValidation';
import { formatFileSize } from '../../utils/stringHelpers';
import type { PublicationSource } from '../../utils/publicationWorkflow';
import type { ProcessingStatus, PublicationDraft, UploadState } from './types';

interface VersionUploadContainerProps {
  dappName: string;
  onSubmit: (draft: PublicationDraft) => void;
  uploadState: UploadState;
  status?: ProcessingStatus;
  errorMessage?: string;
  onClearError: () => void;
  onValidityChange: (isValid: boolean) => void;
  onTouchedChange: (isTouched: boolean) => void;
  isSubmitting: boolean;
  isRepairingPackage?: boolean;
  onRepairPackageCase?: (repair: PackageCaseRepair) => Promise<void> | void;
}

export interface PackageCaseRepair {
  currentPackageName: string;
  newPackageName: string;
}

const PACKAGE_NAME_PATTERN =
  '[A-Za-z][A-Za-z0-9_]*(?:\\.[A-Za-z][A-Za-z0-9_]*)+';
const PACKAGE_MISMATCH_PATTERN = new RegExp(
  `Package name mismatch\\. Expected: (${PACKAGE_NAME_PATTERN}), Got: (${PACKAGE_NAME_PATTERN})\\.`,
);

export function getPackageCaseRepair(
  errorMessage?: string,
): PackageCaseRepair | null {
  const match = errorMessage?.match(PACKAGE_MISMATCH_PATTERN);
  if (!match || match[1] === match[2]) {
    return null;
  }

  return match[1].toLowerCase() === match[2].toLowerCase()
    ? { currentPackageName: match[1], newPackageName: match[2] }
    : null;
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

const standardEase: [number, number, number, number] = [0.4, 0, 0.2, 1];

type MotionDivTransitionProps = Pick<
  HTMLMotionProps<'div'>,
  'initial' | 'animate' | 'exit' | 'transition' | 'style'
>;

const transitionProps: MotionDivTransitionProps = {
  initial: { opacity: 0, scale: 0.95, y: -8, rotateX: 15 },
  animate: { opacity: 1, scale: 1, y: 0, rotateX: 0 },
  exit: { opacity: 0, scale: 0.95, y: 8, rotateX: -15 },
  transition: {
    duration: 0.15,
    ease: standardEase,
    opacity: { duration: 0.1 },
  },
  style: { transformPerspective: 1000 },
};

const panelTransition: MotionDivTransitionProps = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' as const },
  exit: { opacity: 0, height: 0 },
  transition: {
    opacity: { duration: 0.15 },
    height: { duration: 0.3, ease: standardEase },
  },
};

export function VersionUploadContainer({
  dappName,
  onSubmit,
  uploadState,
  status,
  errorMessage,
  onClearError,
  onValidityChange,
  onTouchedChange,
  isSubmitting,
  isRepairingPackage = false,
  onRepairPackageCase,
}: VersionUploadContainerProps) {
  const [sourceKind, setSourceKind] =
    useState<PublicationSource['kind']>('portalUpload');
  const [file, setFile] = useState<File | null>(null);
  const [apkUrl, setApkUrl] = useState('');
  const [whatsNew, setWhatsNew] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const isValid =
      whatsNew.trim().length > 0 &&
      (sourceKind === 'portalUpload'
        ? !!file
        : isHttpsUrl(apkUrl.trim()) && apkUrl.trim().length > 0);

    onValidityChange(isValid);
  }, [apkUrl, file, onValidityChange, sourceKind, whatsNew]);

  const setTouched = () => {
    onTouchedChange(true);
  };

  const handleFileSelect = (selectedFile: File) => {
    if (!hasApkFileExtension(selectedFile.name)) {
      alert(APK_EXTENSION_ERROR_MESSAGE);
      return;
    }

    setFile(selectedFile);
    setTouched();
    if (sourceKind === 'externalUrl') {
      setSourceKind('portalUpload');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const submitDraft = (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || uploadState !== 'form') {
      return;
    }

    if (sourceKind === 'portalUpload' && file) {
      onSubmit({
        source: { kind: 'portalUpload', file },
        whatsNew,
      });
      return;
    }

    if (sourceKind === 'externalUrl' && isHttpsUrl(apkUrl.trim())) {
      onSubmit({
        source: { kind: 'externalUrl', url: apkUrl.trim() },
        whatsNew,
      });
    }
  };

  if (uploadState === 'error') {
    const packageCaseRepair = getPackageCaseRepair(errorMessage);

    const repairPackageCase = async () => {
      if (
        !packageCaseRepair ||
        !onRepairPackageCase ||
        !window.confirm(
          `Change this Draft app and its App NFT package from ${packageCaseRepair.currentPackageName} to ${packageCaseRepair.newPackageName}? This is only allowed before any release exists.`,
        )
      ) {
        return;
      }

      await onRepairPackageCase(packageCaseRepair);
    };

    return (
      <form id="version-form" onSubmit={submitDraft} className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-white mb-2">
            Upload Failed
          </h1>
          <p className="text-neutral-400 text-base">
            There was an error uploading your release
          </p>
        </div>
        <div className="border border-red-800 bg-red-950/20 rounded-lg p-8">
          <div className="flex items-start justify-between gap-6">
            <div className="text-left min-w-0">
              <h3 className="text-red-400 font-medium mb-2">
                Publication failed
              </h3>
              <p className="text-red-300 text-sm break-words">{errorMessage}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {packageCaseRepair && onRepairPackageCase && (
                <button
                  type="button"
                  onClick={repairPackageCase}
                  disabled={isRepairingPackage}
                  className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-50 border border-red-500 rounded-md text-white font-medium transition-colors"
                >
                  {isRepairingPackage ? 'Repairing…' : 'Use APK package'}
                </button>
              )}
              <button
                type="button"
                onClick={onClearError}
                disabled={isRepairingPackage}
                className="px-4 py-2 bg-red-900/50 hover:bg-red-900/70 disabled:opacity-50 border border-red-700 rounded-md text-red-300 font-medium transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </form>
    );
  }

  if (uploadState === 'uploading') {
    const phase = status?.phase;
    const isMinting = phase === 'minting';

    return (
      <form id="version-form" onSubmit={submitDraft} className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-white mb-2">
            {isMinting ? 'Uploading...' : 'Processing...'}
          </h1>
          <p className="text-neutral-400 text-base">
            {isMinting
              ? `Uploading a new release for ${dappName || 'your app'}`
              : 'Processing APK'}
          </p>
        </div>
        <div className="border border-neutral-700 rounded-lg p-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between text-neutral-400 text-sm">
              <div className="flex items-center gap-2">
                {isMinting ? (
                  <CloudUploadIcon className="size-5 fill-white animate-pulse" />
                ) : (
                  <RefreshIcon className="size-5 animate-spin fill-white" />
                )}
                <span className="text-white font-medium">
                  {status?.message || 'Publishing version'}
                </span>
              </div>
              <span>{Math.round(status?.progress || 0)}%</span>
            </div>

            <div className="w-full bg-neutral-800 rounded-full h-2">
              <div
                className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${status?.progress || 0}%` }}
              />
            </div>

            {status?.ingestionSessionId && (
              <p className="text-sm text-neutral-500">
                Session{' '}
                <span className="text-neutral-400">
                  {status.ingestionSessionId}
                </span>
              </p>
            )}
          </div>
        </div>
      </form>
    );
  }

  return (
    <form id="version-form" onSubmit={submitDraft} className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white mb-2">
          Upload {dappName ? `${dappName} ` : ''}release
        </h1>
        <p className="text-neutral-400 text-base">
          Add your upcoming release for the Solana Mobile team to review.
        </p>
      </div>

      <div>
        <span className="block text-white text-base font-medium mb-2">
          dApp File
        </span>
        <div className="relative h-6 mb-4 overflow-hidden">
          <AnimatePresence mode="wait">
            {sourceKind === 'portalUpload' ? (
              <motion.div
                key="upload-description"
                {...transitionProps}
                className="absolute inset-0 flex items-center justify-between"
              >
                <p className="text-neutral-400 text-sm">
                  Upload your Android app file in .APK format.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSourceKind('externalUrl');
                    setTouched();
                  }}
                  className="text-teal-500 hover:text-teal-400 transition-colors text-sm font-medium ml-4 flex-shrink-0"
                >
                  or use APK URL
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="url-description"
                {...transitionProps}
                className="absolute inset-0 flex items-center justify-between"
              >
                <p className="text-neutral-400 text-sm">
                  Provide a stable HTTPS link to your APK.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSourceKind('portalUpload');
                    setTouched();
                  }}
                  className="text-teal-500 hover:text-teal-400 transition-colors text-sm font-medium ml-4 flex-shrink-0"
                >
                  or upload APK
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div style={{ position: 'relative' }}>
          <AnimatePresence mode="wait" initial={false}>
            {sourceKind === 'externalUrl' ? (
              <motion.div key="url-input" {...panelTransition}>
                <div className="rounded-lg border-2 border-dashed border-neutral-700 p-6 space-y-3">
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 fill-neutral-500" />
                    <input
                      type="url"
                      aria-label="APK URL"
                      value={apkUrl}
                      onChange={(event) => {
                        setApkUrl(event.target.value);
                        setTouched();
                      }}
                      className="w-full rounded-lg border border-neutral-700 bg-[#161717] py-3 pl-10 pr-4 text-white placeholder-neutral-500 focus:border-teal-500 focus:outline-none"
                      placeholder="https://example.com/app-release.apk"
                      disabled={isSubmitting}
                    />
                  </div>
                  <p className="text-xs text-neutral-500">
                    The portal will download, validate, and ingest the APK from
                    this URL.
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="upload-area"
                {...panelTransition}
                role="region"
                aria-label="File upload area"
                className={`relative border-2 border-dashed rounded-lg transition-colors ${
                  isDragging
                    ? 'border-teal-500 bg-teal-500/10'
                    : 'border-neutral-700 hover:border-neutral-600'
                } ${file ? 'bg-[#161717]' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".apk"
                  aria-label="APK file"
                  onChange={handleFileChange}
                  className="sr-only"
                  disabled={isSubmitting}
                />

                {file ? (
                  <div className="p-6">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">
                          {file.name}
                        </p>
                        <p className="text-neutral-400 text-sm mt-1">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFile(null)}
                        className="text-teal-500 hover:text-teal-400 transition-colors text-sm font-medium ml-4"
                        disabled={isSubmitting}
                      >
                        Replace
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full p-8 flex flex-col items-center justify-center gap-3 cursor-pointer group"
                    disabled={isSubmitting}
                  >
                    <div className="size-10 bg-neutral-800 rounded-full flex items-center justify-center group-hover:bg-neutral-700 transition-colors">
                      <AddIcon className="size-6 fill-neutral-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-neutral-300 font-medium">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-neutral-500 text-sm mt-1">
                        APK format
                      </p>
                    </div>
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-white text-base font-medium">
          What&apos;s New
        </label>
        <p className="text-neutral-400 text-sm mb-1">
          Tell users what's new in this version
        </p>
        <textarea
          aria-label="What's New"
          value={whatsNew}
          onChange={(event) => {
            setWhatsNew(event.target.value);
            setTouched();
          }}
          rows={4}
          className="w-full rounded-lg border border-neutral-700 bg-[#161717] px-4 py-3 text-white placeholder-neutral-500 focus:border-teal-500 focus:outline-none hover:border-neutral-600 transition-colors"
          placeholder={
            '• New features and improvements\n• Performance enhancements\n• UI updates'
          }
          disabled={isSubmitting}
        />
      </div>
    </form>
  );
}
